'use client'

import { GlobalTypography, ProductPage } from 'cms-types'
import { ReadonlyURLSearchParams, useRouter, useSearchParams } from 'next/navigation'
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { getShopifyGID } from 'shopify-types/helpers'
import {
  addAlert,
  GrouppedOptionsByName,
  MappedProductOptions,
  OptionType,
  type ProductDataForTags,
  ProductDetailsGallery,
  ProductIntro,
  ProductVariant,
  ProductVariantsInfo,
} from 'ui'

import { pageToUrl } from '@/cms/helpers'
import { getLocaleFromHostname } from '@/helpers/getLocaleFromHostname'
import { pushGTMEvent } from '@/helpers/googleTagManager'
import { subscribeToBackInStock, subscribeToNewsletter } from '@/helpers/klaviyo'
import { logger } from '@/helpers/logger'
import { getValidTagsForOption } from '@/helpers/mapProductOptions'
import { getTextFromRichText, richTextToJSX } from '@/helpers/richTextParser'
import { useSSR } from '@/hooks/useSSR'
import { useUrlValidation } from '@/hooks/useUrlValidation'
import { useCart } from '@/store/cartStore'
import { usePreviousPath } from '@/store/previousPathStore'

type ProductBlockHOCProps = {
  variants: ProductVariant[]
  productIntro: ProductPage['productIntro']
  title: string
  variantsInfo: ProductVariantsInfo
  shopifyDescription: string
  typography: GlobalTypography['productPage']
  shippingDetails?: {
    text: string
    icon?: string | null
  }[]
  galleryItems: ProductDetailsGallery
  productOptions: MappedProductOptions
  initialVariantId?: string
}

const isEmpty = (obj: unknown) =>
  Array.isArray(obj) ? !obj.length : !Object.entries(obj || {}).length

const isEqual = (obj1: Record<string, string>, obj2: Record<string, string>) => {
  const props1 = Object.getOwnPropertyNames(obj1)
  const props2 = Object.getOwnPropertyNames(obj2)

  if (props1.length != props2.length) return false

  for (const prop of props1) {
    if (obj1[prop] !== obj2[prop]) return false
  }
  return true
}

/**
 * `useSearchParams` makes the closest Suspense boundary client-side rendered on static pages, so
 * the server renders the default variant as fallback for crawlers, and the client renders the
 * variant from the URL. See https://nextjs.org/docs/app/api-reference/functions/use-search-params#prerendering
 */
export default function ProductBlockHOC(props: ProductBlockHOCProps) {
  return (
    <Suspense fallback={<ProductBlock {...props} searchParams={null} />}>
      <ProductBlockWithSearchParams {...props} />
    </Suspense>
  )
}

function ProductBlockWithSearchParams(props: ProductBlockHOCProps) {
  const searchParams = useSearchParams()

  return <ProductBlock {...props} searchParams={searchParams} />
}

function ProductBlock({
  searchParams,
  variants,
  variantsInfo,
  productIntro,
  title,
  typography,
  shippingDetails,
  galleryItems,
  shopifyDescription,
  productOptions,
  initialVariantId,
}: ProductBlockHOCProps & { searchParams: ReadonlyURLSearchParams | null }) {
  const [outOfStockSubSuccess, setOutOfStockSubSuccess] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const router = useRouter()

  const cart = useCart()
  const { prevPath } = usePreviousPath()
  const { isServer } = useSSR()

  const [tagListSelectedOptions, setTagListSelectedOptions] = useState<GrouppedOptionsByName>(
    {} as GrouppedOptionsByName,
  )
  const [selectSelectedOptions, setSelectSelectedOptions] = useState<GrouppedOptionsByName>(
    {} as GrouppedOptionsByName,
  )

  const { mappedVariants, optionsForSelect } = productOptions

  const [tagListOptions, setTagListOptions] = useState<ProductDataForTags[]>([])
  const defaultVariantId = variants[0].id.split('/').at(-1) ?? null
  const [selectedVariant, setSelectedVariant] = useState<string | null>(
    searchParams?.get('variant') ?? initialVariantId ?? defaultVariantId,
  )
  const [quantity, setQuantity] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const currentVariant = useMemo(
    () =>
      selectedVariant == null ? null : (variants.find(({ id }) => id === selectedVariant) ?? null),
    [selectedVariant, variants],
  )

  const shopifyVariantGID = useMemo(() => {
    if (!currentVariant) {
      return
    }

    return getShopifyGID(currentVariant.id, 'ProductVariant')
  }, [currentVariant])

  const productDetailsGallery = useMemo(
    () => (selectedVariant ? (galleryItems[selectedVariant] ?? []) : null),
    [selectedVariant, galleryItems],
  )

  const productInfo = useMemo(() => {
    if (!selectedVariant || !(selectedVariant in variantsInfo)) {
      return {
        description: '',
        accordion: [],
      }
    }

    const { description, accordion = [] } = variantsInfo[selectedVariant]

    const parsedDescription = getTextFromRichText(description)
      ? richTextToJSX(description)
      : shopifyDescription

    const parsedAccordion =
      accordion?.map(({ title = '', content }) => ({
        title,
        content: richTextToJSX(content),
      })) ?? []

    return { description: parsedDescription, accordion: parsedAccordion }
  }, [selectedVariant, variantsInfo, shopifyDescription])

  const backButton = useMemo(() => {
    const { backButton } = typography

    const url = prevPath || (pageToUrl(backButton?.fallbackLink?.page) ?? '')
    const text =
      prevPath || isServer
        ? (backButton?.backLinkText ?? '')
        : (backButton?.fallbackLink?.title ?? backButton?.backLinkText ?? '')

    return {
      text,
      url,
    }
  }, [typography, prevPath, isServer])

  const helperText = useMemo(() => {
    if (currentVariant?.priceToReferenceValue != null) {
      return `${currentVariant.priceToReferenceValue}${productIntro?.helperText}`
    }
  }, [currentVariant?.priceToReferenceValue, productIntro?.helperText])

  const onAddItemToCheckout = useCallback(async () => {
    if (!currentVariant || !shopifyVariantGID) return

    setIsSubmitting(true)

    try {
      await cart.addToBasket([
        {
          variantId: shopifyVariantGID,
          quantity,
        },
      ])
      pushGTMEvent([{ title, variant: currentVariant, quantity }])

      cart.setIsOpen(true)
    } catch (e) {
      logger.error('Produkte > onAddItemToCheckout', e)

      addAlert.error('Fehler beim Hinzufügen des Produkts')
    } finally {
      setIsSubmitting(false)
    }
  }, [currentVariant, shopifyVariantGID, cart, quantity, title])

  const onBuyNow = useCallback(async () => {
    if (!currentVariant || !shopifyVariantGID) return

    setIsSubmitting(true)

    try {
      const checkoutUrl = await cart.createBuyNowCheckoutUrl(shopifyVariantGID, quantity)

      const url = new URL(checkoutUrl)
      url.searchParams.set('t', Date.now().toString())
      url.searchParams.set('locale', getLocaleFromHostname(window.location.hostname))

      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const klaviyoId = (window._learnq as any).push(['_getIdentifiers'])['$exchange_id']
        url.searchParams.set('klaviyoId', klaviyoId)
      } catch (e) {
        logger.warn('Produkte > onBuyNow > klaviyo', e)
      }

      pushGTMEvent([{ title, variant: currentVariant, quantity }], 'begin_checkout')

      router.push(url.toString())
    } catch (e) {
      logger.error('Produkte > onBuyNow', e)

      addAlert.error('Fehler beim Weiterleiten zur Kasse')
    } finally {
      setIsSubmitting(false)
    }
  }, [currentVariant, shopifyVariantGID, cart, quantity, title, router])

  const onSelectedOptionChange = useCallback(
    (options: GrouppedOptionsByName, type: OptionType) => {
      const updateOptions = type === 'select' ? setSelectSelectedOptions : setTagListSelectedOptions

      updateOptions(prev => {
        const updatedOptions = { ...prev, ...options }

        if (type === 'select') {
          const validTagValues = getValidTagsForOption(options, optionsForSelect, mappedVariants)
          setTagListOptions(validTagValues)

          //in case of first load when tags exist but the selected tag has not been set, we need to set the selected tag
          if (
            !isEmpty(updatedOptions) &&
            isEmpty(tagListSelectedOptions) &&
            !isEmpty(validTagValues) &&
            Object.keys(updatedOptions).every(ele => {
              return (
                updatedOptions[ele as keyof GrouppedOptionsByName] ===
                currentVariant?.selectedOptions[ele]
              )
            })
          ) {
            const updatedTagListSelectedOptions = Object.fromEntries(
              Object.entries(currentVariant?.selectedOptions ?? {}).filter(
                ([key, value]) =>
                  !Object.entries(updatedOptions).find(([selectKey, selectValue]) => {
                    return selectKey === key && selectValue === value
                  }),
              ),
            ) as GrouppedOptionsByName

            onSelectedOptionChange(updatedTagListSelectedOptions, 'tags')
          }
        }

        return updatedOptions
      })
    },
    [currentVariant?.selectedOptions, mappedVariants, optionsForSelect, tagListSelectedOptions],
  )

  const onSubscribe = useCallback(
    async (email: string, newsletter: boolean) => {
      try {
        if (!currentVariant?.id) return

        await subscribeToBackInStock(email, currentVariant?.id)
        if (newsletter) {
          await subscribeToNewsletter(email)
        }

        setOutOfStockSubSuccess(true)
        setSuccessMessage(
          typography.itemIsOutOfStockEmailNotificationSuccessMessage +
            (newsletter
              ? '\n' + typography.itemIsOutOfStockEmailNotificationSuccessMessageNewsletterAdded
              : ''),
        )
      } catch (error) {
        logger.error('Produkte > onSubscribe', error)
        addAlert.error('Fehler beim Abonnieren')
      }
    },
    [
      currentVariant?.id,
      typography.itemIsOutOfStockEmailNotificationSuccessMessage,
      typography.itemIsOutOfStockEmailNotificationSuccessMessageNewsletterAdded,
    ],
  )

  useEffect(() => {
    if (!currentVariant) return
    pushGTMEvent([{ title, variant: currentVariant, quantity }], 'view_item')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant])

  useEffect(() => {
    if (isEmpty(optionsForSelect)) {
      return
    }

    const options = { ...tagListSelectedOptions, ...selectSelectedOptions }

    if (isEmpty(options)) {
      return
    }

    const selectedVariant = mappedVariants.find(({ selectedOptions }) => {
      if (Object.keys(options).length !== Object.keys(selectedOptions).length) {
        return false
      }

      return isEqual(selectedOptions, options)
    })

    // when select and tags exits, the valid tags are updated based on the selected select options, however on first load, the tagListSelectedOptions are not set properly
    if (
      !selectedVariant &&
      !isEmpty(selectSelectedOptions) &&
      !isEmpty(tagListSelectedOptions) &&
      !isEmpty(tagListOptions)
    ) {
      const fallbackSelectedVariant = mappedVariants.find(({ selectedOptions }) => {
        return Object.keys(selectSelectedOptions).every(ele => {
          return selectSelectedOptions[ele as keyof GrouppedOptionsByName] === selectedOptions[ele]
        })
      })
      if (fallbackSelectedVariant && fallbackSelectedVariant.id !== currentVariant?.id) {
        //filter out the selectSelectedOptions
        const updatedTagListSelectedOptions = Object.fromEntries(
          Object.entries(fallbackSelectedVariant.selectedOptions).filter(
            ([key, value]) =>
              !Object.entries(selectSelectedOptions).find(([selectKey, selectValue]) => {
                return selectKey === key && selectValue === value
              }),
          ),
        ) as GrouppedOptionsByName

        onSelectedOptionChange(updatedTagListSelectedOptions, 'tags')
        return
      }
    }

    if (!selectedVariant || currentVariant?.id === selectedVariant.id) {
      return
    }

    onVariantChange(selectedVariant.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagListSelectedOptions, selectSelectedOptions, optionsForSelect, mappedVariants])

  const onVariantChange = useCallback(
    (selectedVariantId: string) => {
      const newUrl = new URL(window.location.href)
      newUrl.searchParams.set('variant', selectedVariantId)

      setSelectedVariant(selectedVariantId)

      router.replace(newUrl.toString())
    },
    [router],
  )

  useUrlValidation({
    searchParams,
    pathCheckRules: [
      {
        test: (pathname: string) => pathname.startsWith('/produkte'),
        getValidPathname: (pathname: string) => `/produkte/${pathname}`,
      },
    ],
    searchParamsCheckRules: [
      {
        key: 'variant',
        testValue: value => variants.some(({ id }) => id.includes(value)),
        defaultValue: defaultVariantId ?? undefined,
        onFail: () => {
          setSelectedVariant(defaultVariantId)
        },
        onSuccess: setSelectedVariant,
      },
    ],
  })

  return (
    <div className='mx-auto xl:px-10'>
      <ProductIntro
        data={{
          variants,
          productOptions,
          tagListOptions,
          tagListSelectedOptions,
          selectSelectedOptions,
          title,
          currentVariant,
          isProductAvailable: currentVariant?.isAvailable ?? false,
          isAddButtonDisabled: isSubmitting || !currentVariant?.isAvailable,
          quantity,
          onCountChange: setQuantity,
          onAddItemToCheckout,
          onBuyNow: typography.showBuyNowButton ? onBuyNow : undefined,
          buyNowButtonText: typography.showBuyNowButton ? typography.buyNowButtonText : undefined,
          shopPay:
            typography.showShopPayButton && shopifyVariantGID
              ? {
                  variantId: shopifyVariantGID,
                  quantity,
                  storeDomain: `https://${process.env.NEXT_PUBLIC_SHOPIFY_HOST ?? ''}`,
                }
              : null,
          onVariantChange,
          onSubscribe,
          shippingDetails,
          productInfo,
          reviews: currentVariant?.reviews.ratings,
          addToCartButtonText:
            currentVariant != null && !currentVariant.isAvailable
              ? typography.itemIsOutOfStockButtonText
              : typography.addToCartButtonText,
          emailNotificationDescription:
            richTextToJSX(typography.itemIsOutOfStockEmailNotificationText) || [],
          accessibility: typography.accessibility,
          itemIsOutOfStockFormSuccess: outOfStockSubSuccess,
          itemIsOutOfStockFormSuccessMessage: successMessage,
          itemIsOutOfStockButtonText: typography.itemIsOutOfStockButtonText,
          itemIsOutOfStockEmailNotificationInput: typography.itemIsOutOfStockEmailNotificationInput,
          itemIsOutOfStockEmailNotificationNewsletterCheckboxText:
            typography.itemIsOutOfStockEmailNotificationNewsletterCheckboxText,
          itemIsOutOfStockEmailNotificationButtonText:
            typography.itemIsOutOfStockEmailNotificationButtonText,
          reviewsText: typography.reviewsText,
          helperText,
          galleryItems: productDetailsGallery,
          backButton,
          onSelectedOptionChange,
        }}
      />
    </div>
  )
}
